import { useEffect, useState } from 'react'
import { X, Download } from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Tabs, Rows, Empty, statusBadge } from '../components/UI'
import { get, del } from '../lib/api'
import { toast } from '../store'
import { clsx, num, faDate, shortAddr, asArray} from '../lib/format'

const KIND_LABEL: Record<string, string> = {
  deposit: 'واریز', withdraw: 'برداشت', fee: 'کارمزد', reward: 'پاداش', referral: 'پورسانت',
  transfer_out: 'انتقال داخلی (ارسال)', transfer_in: 'انتقال داخلی (دریافت)',
}

export default function History() {
  const [tab, setTab] = useState('orders')
  const [rows, setRows] = useState<any[] | null>(null)
  const [filter, setFilter] = useState('')

  const load = () => {
    setRows(null)
    const url =
      tab === 'orders' ? `/orders?limit=100${filter ? `&status=${filter}` : ''}`
      : tab === 'trades' ? '/orders/trades?limit=100'
      : `/wallet/transactions?limit=100${filter ? `&kind=${filter}` : ''}`
    get(url).then((r: any) => setRows(asArray(r))).catch(() => setRows([]))
  }
  useEffect(load, [tab, filter])
  useEffect(() => setFilter(''), [tab])

  const cancel = async (id: number) => {
    try { await del(`/orders/${id}`); toast.ok('سفارش لغو شد'); load() }
    catch (e: any) { toast.err(e.message) }
  }

  const exportCsv = () => {
    if (!rows?.length) return
    const keys = Object.keys(rows[0])
    const csv = [keys.join(','), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? '')).join(','))].join('\n')
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url; a.download = `arzino-${tab}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <PanelShell title="سفارش‌ها و تراکنش‌ها" subtitle="تاریخچه کامل فعالیت‌های حساب شما">
      <Card className="!p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-white/[.06] p-4">
          <Tabs value={tab} onChange={setTab} size="sm"
            items={[{ key: 'orders', label: 'سفارش‌ها' }, { key: 'trades', label: 'معاملات' }, { key: 'txs', label: 'واریز و برداشت' }]} />
          {tab !== 'trades' && (
            <select className="input !w-auto !py-2 text-xs" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="" className="bg-ink-900">همه</option>
              {tab === 'orders'
                ? [['open', 'باز'], ['filled', 'تکمیل‌شده'], ['canceled', 'لغو‌شده']].map(([v, l]) =>
                    <option key={v} value={v} className="bg-ink-900">{l}</option>)
                : [['deposit', 'واریز'], ['withdraw', 'برداشت'], ['fee', 'کارمزد'], ['reward', 'پاداش']].map(([v, l]) =>
                    <option key={v} value={v} className="bg-ink-900">{l}</option>)}
            </select>
          )}
          <button onClick={exportCsv} className="btn-ghost mr-auto !py-2 text-xs">
            <Download className="h-4 w-4" /> خروجی CSV
          </button>
        </div>

        {/* ── موبایل: هر تراکنش/سفارش یک کارت.
            جدول هفت‌ستونه روی گوشی ۶۸۰ پیکسل عرض می‌خواست و کل صفحه را افقی
            می‌کرد؛ همان اطلاعات اینجا در دو سطر خوانده می‌شود. */}
        <div className="p-2 md:hidden">
          {rows === null ? <div className="p-3"><Rows n={5} /></div>
            : rows.length === 0 ? <Empty text="موردی برای نمایش وجود ندارد" />
            : tab === 'txs' ? (
              <div className="space-y-2">
                {rows.map((t) => (
                  <div key={t.id} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <span className={clsx('text-xs font-bold', t.kind === 'withdraw' ? 'text-down' : 'text-up')}>
                        {KIND_LABEL[t.kind] ?? t.kind}
                      </span>
                      <span className="num text-xs text-slate-300">{t.currency}</span>
                      <span className="mr-auto">{statusBadge(t.status)}</span>
                    </div>
                    <div className="num mb-1 text-base font-semibold text-white">{num(t.amount, 6)}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                      {t.fee > 0 && <span className="num">کارمزد {num(t.fee, 6)}</span>}
                      {(t.network || t.address) && (
                        <span className="num" dir="ltr">{t.network || shortAddr(t.address)}</span>
                      )}
                      <span className="mr-auto">{faDate(t.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {rows.map((o) => (
                  <div key={o.id} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <span className={clsx('text-xs font-bold',
                        o.side === 'buy' ? 'text-up' : o.side === 'sell' ? 'text-down' : 'text-brand-200')}>
                        {o.side === 'buy' ? 'خرید' : o.side === 'sell' ? 'فروش' : 'تبدیل'}
                      </span>
                      <span className="num text-xs text-slate-200" dir="ltr">{o.symbol}</span>
                      <span className="mr-auto">{statusBadge(o.status ?? 'filled')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-y-1 text-[11px]">
                      <span className="text-slate-500">مقدار</span>
                      <span className="num text-left text-slate-200">{num(o.amount, 6)}</span>
                      <span className="text-slate-500">قیمت</span>
                      <span className="num text-left text-slate-300">{num(o.price ?? o.avgPrice)}</span>
                      <span className="text-slate-500">مبلغ کل</span>
                      <span className="num text-left text-white">{num(o.total)}</span>
                    </div>
                    <div className="mt-1.5 text-[10px] text-slate-500">{faDate(o.createdAt)}</div>
                  </div>
                ))}
              </div>
            )}
        </div>

        <div className="hidden overflow-x-auto p-2 md:block">
          {rows === null ? <div className="p-3"><Rows n={6} /></div>
            : rows.length === 0 ? <Empty text="موردی برای نمایش وجود ندارد" />
            : tab === 'txs' ? (
              <table className="w-full min-w-[680px] text-xs">
                <thead><tr className="text-[11px] text-slate-500">
                  {['نوع', 'ارز', 'مبلغ', 'کارمزد', 'شبکه/مقصد', 'وضعیت', 'زمان'].map((h) =>
                    <th key={h} className="px-3 py-2 text-right font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className="border-t border-white/[.05]">
                      <td className={clsx('px-3 py-2.5 font-semibold', t.kind === 'withdraw' ? 'text-down' : 'text-up')}>
                        {KIND_LABEL[t.kind] ?? t.kind}</td>
                      <td className="num px-3 py-2.5 text-slate-300">{t.currency}</td>
                      <td className="num px-3 py-2.5 text-white">{num(t.amount, 6)}</td>
                      <td className="num px-3 py-2.5 text-slate-500">{num(t.fee, 6)}</td>
                      <td className="num px-3 py-2.5 text-slate-500" dir="ltr">{t.network || shortAddr(t.address) || '—'}</td>
                      <td className="px-3 py-2.5">{statusBadge(t.status)}</td>
                      <td className="px-3 py-2.5 text-slate-500">{faDate(t.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full min-w-[680px] text-xs">
                <thead><tr className="text-[11px] text-slate-500">
                  {['بازار', 'سمت', 'نوع', 'قیمت', 'مقدار', 'مبلغ کل', 'وضعیت', 'زمان', ''].map((h, i) =>
                    <th key={i} className="px-3 py-2 text-right font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((o) => (
                    <tr key={o.id} className="border-t border-white/[.05]">
                      <td className="num px-3 py-2.5 text-slate-200" dir="ltr">{o.symbol}</td>
                      <td className={clsx('px-3 py-2.5 font-semibold',
                        o.side === 'buy' ? 'text-up' : o.side === 'sell' ? 'text-down' : 'text-brand-200')}>
                        {o.side === 'buy' ? 'خرید' : o.side === 'sell' ? 'فروش' : 'تبدیل'}</td>
                      <td className="px-3 py-2.5 text-slate-400">
                        {o.type === 'limit' ? 'حد' : o.type === 'market' ? 'بازار' : o.type === 'stop' ? 'حد ضرر' : o.kind === 'convert' ? 'تبدیل' : '—'}</td>
                      <td className="num px-3 py-2.5 text-slate-300">{num(o.price ?? o.avgPrice)}</td>
                      <td className="num px-3 py-2.5 text-slate-300">{num(o.amount, 6)}</td>
                      <td className="num px-3 py-2.5 text-white">{num(o.total)}</td>
                      <td className="px-3 py-2.5">{statusBadge(o.status ?? 'filled')}</td>
                      <td className="px-3 py-2.5 text-slate-500">{faDate(o.createdAt)}</td>
                      <td className="px-3 py-2.5 text-left">
                        {['open', 'partial'].includes(o.status) && (
                          <button onClick={() => cancel(o.id)} className="rounded-lg p-1.5 text-slate-500 hover:bg-down/10 hover:text-down">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </Card>
    </PanelShell>
  )
}
